import {AxiosError, AxiosResponse, isAxiosError} from 'axios';
import {createEffect, createStore, restore, sample} from 'effector';
import {createAction} from 'effector-action';
import {createGate} from 'effector-react';
import {combineEvents} from 'patronum';

import {Configs, Environment} from '#bunker/types';
import {initLoggerFx, logErrorFx, logEventStringFx} from '#logger';
import {configApi, GetConfigResponse} from 'api/configs';
import {getPermissions} from 'models/permissions';

import {DEFAULT_CONFIGS_RESULT, LOCAL_STORAGE_KEY, LOCAL_STORAGE_TTL} from './consts';
import {GetConfigsError, GetConfigsResult} from './types';
import {getAuthproxyHost, getCachedData} from './utils';

export const ConfigsGate = createGate();

const $result = createStore<GetConfigsResult>(DEFAULT_CONFIGS_RESULT);

export const $configs = createStore<Configs>({});
export const $login = createStore<string | null>(null);
export const $env = createStore<Environment>('production');
export const $authproxyHost = createStore<string | null>(null);
export const $calltechHost = createStore<string | null>(null);
export const $version = createStore<string | null>(null);

export const getConfigsFx =
    createEffect<void, AxiosResponse<GetConfigResponse>, AxiosError<GetConfigsError> | Error>(
        () => configApi.getConfig(),
    );

export const $configsError = createStore<string | null>(null).reset([ConfigsGate.open, getConfigsFx.done]);

const getConfigsFromLocalStorageFx = createEffect(() => getCachedData());
const $configsFromLS = restore(getConfigsFromLocalStorageFx.doneData, null);

sample({
    clock: ConfigsGate.open,
    target: getConfigsFromLocalStorageFx,
});

const $isUpdateRequired = createStore<boolean | null>(null);

sample({
    clock: getConfigsFromLocalStorageFx.doneData,
    fn: cachedData => {
        const currentTime = Date.now();
        return !cachedData?.data || !(currentTime - cachedData?.timestamp < LOCAL_STORAGE_TTL);
    },
    target: $isUpdateRequired,
});

sample({
    clock: $isUpdateRequired,
    source: $configsFromLS,
    filter: (_, isUpdateRequired) => isUpdateRequired === false,
    fn: cachedData => ({
        configs: cachedData?.data?.data?.appData?.configs || DEFAULT_CONFIGS_RESULT.configs,
        login: cachedData?.data?.data.appData.user.login || DEFAULT_CONFIGS_RESULT.login,
        env: cachedData?.data?.data.appData.env || DEFAULT_CONFIGS_RESULT.env,
        appConfig: cachedData?.data?.data.appData.appConfig || DEFAULT_CONFIGS_RESULT.appConfig,
        version: cachedData?.data?.data.version,
    }),
    target: $result,
});

sample({
    clock: $isUpdateRequired,
    source: $configsFromLS,
    filter: (_, isUpdateRequired) => isUpdateRequired === false,
    fn: cachedData => cachedData?.data,
    target: initLoggerFx,
});

sample({
    clock: initLoggerFx.doneData,
    source: $isUpdateRequired,
    filter: isUpdateRequired => isUpdateRequired === false,
    fn: () => 'Using configs from cache',
    target: logEventStringFx,
});

sample({
    clock: $isUpdateRequired,
    filter: Boolean,
    target: getConfigsFx,
});

sample({
    clock: getConfigsFx.doneData,
    fn: ({data}) => ({
        configs: data.appData.configs || DEFAULT_CONFIGS_RESULT.configs,
        login: data.appData.user.login || DEFAULT_CONFIGS_RESULT.login,
        env: data.appData.env || DEFAULT_CONFIGS_RESULT.env,
        appConfig: data.appData.appConfig || DEFAULT_CONFIGS_RESULT.appConfig,
        version: data.version,
    }),
    target: $result,
});

const setLSConfigsFx = createEffect((data: AxiosResponse<GetConfigResponse, any>) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({data, timestamp: Date.now()}));
});

sample({
    clock: getConfigsFx.doneData,
    target: [setLSConfigsFx, initLoggerFx],
});

sample({
    clock: combineEvents([getConfigsFx.doneData, initLoggerFx.doneData]),
    fn: () => 'Fetched configs from server',
    target: logEventStringFx,
});

sample({
    clock: getConfigsFx.failData,
    source: $configsFromLS,
    fn: cachedData => cachedData?.data || undefined,
    target: initLoggerFx,
});

sample({
    clock: getConfigsFx.failData,
    source: $configsFromLS,
    fn: cachedData => {
        return {
            configs: cachedData?.data?.data?.appData?.configs || DEFAULT_CONFIGS_RESULT.configs,
            login: cachedData?.data?.data?.appData?.user?.login || DEFAULT_CONFIGS_RESULT.login,
            env: cachedData?.data?.data?.appData?.env || DEFAULT_CONFIGS_RESULT.env,
            appConfig: cachedData?.data?.data?.appData?.appConfig || DEFAULT_CONFIGS_RESULT.appConfig,
            version: cachedData?.data?.data.version || DEFAULT_CONFIGS_RESULT.version,
        };
    },
    target: $result,
});

const getConfigxFxError = restore(getConfigsFx.failData, null);

sample({
    clock: combineEvents([getConfigsFx.failData, initLoggerFx.doneData]),
    source: {
        cachedData: $configsFromLS,
        error: getConfigxFxError,
    },
    fn: ({cachedData, error}) => ({
        params: {
            message: cachedData?.data
                ? 'Error fetching configs from server, using cached data'
                : 'Error fetching configs from server',
        },
        error,
    }),
    target: logErrorFx,
});

sample({
    clock: getConfigsFx.failData,
    fn: error => {
        if (isAxiosError(error as AxiosError)) {
            return `${(error as AxiosError).status} - ${(error as AxiosError<GetConfigsError>).response?.data?.message}`;
        }
        if (error) {
            return `Configs loading error: ${(error as unknown as Error)?.message}`;
        }
        return null;
    },
    target: $configsError,
});

createAction({
    clock: $result,
    target: {
        $configs,
        $login,
        $env,
        $authproxyHost,
        $calltechHost,
        $version,
    },
    fn: (target, clock) => {
        target.$configs(clock.configs);
        target.$login(clock.login);
        target.$env(clock.env);
        target.$authproxyHost(getAuthproxyHost(clock.appConfig));
        target.$calltechHost(clock.appConfig?.calltechHost || null);
        target.$version(clock.version);
    },
});

const setServerDataFx = createEffect(({configs, login, env, appConfig, version}: GetConfigsResult) => {
    if (!window.serverData) {
        return;
    }
    window.serverData.backendConfigs = configs;
    window.serverData.configs3 = configs;
    window.serverData.login = login;
    window.serverData.env = env;
    window.serverData.authproxyHost = getAuthproxyHost(appConfig);
    window.serverData.passportHost = appConfig?.passportHost || window.serverData.passportHost;
    window.serverData.version = version;
});

sample({
    clock: $result,
    target: setServerDataFx,
});

sample({
    clock: $authproxyHost,
    filter: Boolean,
    target: getPermissions,
});
